import type { HydratedBlogPost } from '../src/types/blog';

/**
 * Pure related-post ranking for the blog (item 10).
 *
 * Kept firebase-free (no '../firebase' import) so it can be unit-tested in
 * isolation, mirroring lib/readTime.ts. services/blogService.ts wires the data
 * fetch (getPublishedPosts) to this scorer in getRelatedPosts.
 */

/**
 * Per-facet scoring weights — FLAT: every shared tag counts equally (a simple
 * overlap count), with a small same-category nudge. Deliberately not tiered —
 * style no longer dominates region/audience.
 *
 * These are a reasonable DEFAULT chosen against untagged seed posts, so they
 * have zero observable effect today (an untagged corpus scores purely on
 * category + recency). Revisit once real, tagged post-flip content exists.
 */
export const RELATED_WEIGHTS = {
  style: 1,
  region: 1,
  audience: 1,
  category: 0.5,
} as const;

/** Count of values shared between two tag arrays (either may be undefined/empty). */
export const overlapCount = (
  a: readonly string[] | undefined,
  b: readonly string[] | undefined
): number => {
  if (!a || !b || a.length === 0 || b.length === 0) return 0;
  const set = new Set(b);
  let count = 0;
  for (const value of a) {
    if (set.has(value)) count += 1;
  }
  return count;
};

/**
 * Rank a candidate corpus by relatedness to `target`, returning the top
 * `limitCount` posts (excluding the target itself).
 *
 * Eligibility gate: a candidate counts as "related" only if it shares the
 * target's CATEGORY or shares ≥1 faceted tag (style/region/audience). This gate
 * is what preserves backwards-compatibility: for an ALL-UNTAGGED corpus the only
 * possible signal is category, so the output collapses to "same-category, newest
 * first, top N, self excluded" — exactly what the legacy
 * getPublishedPostsByCategory(category, 4) → filter(self) → slice(3) produced.
 * Tagged posts additionally surface cross-category relatives that share a tag.
 *
 * Score = (sharedStyles + sharedRegions + sharedAudience) × 1 + sameCategory×0.5
 * (flat per-tag overlap count — see RELATED_WEIGHTS). Ties break by recency
 * (published_at desc). Order-independent of input order.
 */
export const rankRelatedPosts = (
  target: HydratedBlogPost,
  candidates: HydratedBlogPost[],
  limitCount: number
): HydratedBlogPost[] => {
  const scored = candidates
    .filter((candidate) => candidate.id !== target.id)
    .map((candidate) => {
      const styleOverlap = overlapCount(target.styles, candidate.styles);
      const regionOverlap = overlapCount(target.regions, candidate.regions);
      const audienceOverlap = overlapCount(target.audience, candidate.audience);
      const sameCategory = candidate.category === target.category;
      const hasTagOverlap =
        styleOverlap > 0 || regionOverlap > 0 || audienceOverlap > 0;

      const score =
        styleOverlap * RELATED_WEIGHTS.style +
        regionOverlap * RELATED_WEIGHTS.region +
        audienceOverlap * RELATED_WEIGHTS.audience +
        (sameCategory ? RELATED_WEIGHTS.category : 0);

      return {
        post: candidate,
        score,
        eligible: sameCategory || hasTagOverlap,
      };
    })
    .filter((entry) => entry.eligible);

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    // Recency tiebreak — newest first.
    return b.post.published_at.getTime() - a.post.published_at.getTime();
  });

  return scored.slice(0, Math.max(0, limitCount)).map((entry) => entry.post);
};
